import { defaultTime, isDateTimeValue, isDayKey, joinDateTime } from './datePickerModel';

/**
 * FIX-B41 (QX-931 / K4) - typing a date by hand is ALWAYS dd/mm/yyyy (date+time: dd/mm/yyyy hh:mm),
 * whatever the browser locale. The native field orders its segments by the browser locale (mm/dd/yyyy
 * in an en-US Chrome), so typing 15102026 there gave 06/10/2026. The digits are therefore taken by us:
 * the field's keydown is stopped, the digits are collected here, shown in the overlay as a mask, and
 * once complete the value is written to the field as `yyyy-mm-dd` (`yyyy-mm-ddTHH:mm`) through the
 * native setter + input/change events - the same way the calendar (DatePickerLayer) writes it, so
 * controlled and uncontrolled fields, forms and what the server receives do not change.
 */

/** How many digits complete the field. */
export function typedLength(withTime: boolean): number {
  return withTime ? 12 : 8;
}

/** The overlay text while typing: the placeholder with the typed digits filled in from the start. */
export function typedMask(digits: string, withTime: boolean): string {
  const mask = withTime ? 'dd/mm/yyyy hh:mm' : 'dd/mm/yyyy';
  let i = 0;
  return mask.replace(/[dmyh]/g, (ch) => (i < digits.length ? digits[i++] : ch));
}

/** dd mm yyyy [hh mm] digits -> the field value, or null when not a real date / time. */
export function typedToValue(digits: string, withTime: boolean): string | null {
  if (!/^\d+$/.test(digits)) return null;
  if (digits.length === 8) {
    const key = `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
    return isDayKey(key) && digits.slice(4, 8) >= '1000' ? key : null;
  }
  if (withTime && digits.length === 12) {
    const day = typedToValue(digits.slice(0, 8), false);
    const v = day ? joinDateTime(day, `${digits.slice(8, 10)}:${digits.slice(10, 12)}`) : '';
    return isDateTimeValue(v) ? v : null;
  }
  return null;
}

export interface TypingField {
  value: string;
  readOnly?: boolean;
  disabled?: boolean;
  addEventListener(type: string, fn: (e: any) => void): void;
  removeEventListener(type: string, fn: (e: any) => void): void;
}
export interface TypingKey {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  preventDefault(): void;
}

/**
 * Takes over the field's digit typing. `onDraft` gets the mask while typing and null when done / cancelled;
 * `write` puts a complete value in the field (native setter + events). Returns the detach function.
 * - digits: collected (the browser never sees them); complete + valid -> written;
 * - Backspace / Delete: removes the last typed digit; with nothing typed it empties the field (as the native one does);
 * - separators (/ . - : space) are ignored - the mask has them;
 * - Escape or leaving the field drops an unfinished draft (the previous value stays). Leaving a date+time field
 *   with a complete DATE takes the field's time, else now (the same default the calendar uses).
 * Arrows, Enter, Tab and shortcuts are not touched (the calendar layer drives the arrows / Enter).
 */
export function attachDmyTyping(
  el: TypingField,
  withTime: boolean,
  onDraft: (mask: string | null) => void,
  write: (value: string) => void,
  now: () => Date = () => new Date(),
): () => void {
  let digits = '';
  const reset = () => { if (digits) { digits = ''; onDraft(null); } };
  const onKey = (e: TypingKey) => {
    if (el.readOnly || el.disabled || e.ctrlKey || e.metaKey || e.altKey) return;
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      if (digits.length >= typedLength(withTime)) digits = '';
      digits += e.key;
      if (digits.length === typedLength(withTime)) {
        const v = typedToValue(digits, withTime);
        if (v) { digits = ''; onDraft(null); write(v); return; }
      }
      onDraft(typedMask(digits, withTime));
      return;
    }
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      if (digits) {
        digits = digits.slice(0, -1);
        onDraft(digits ? typedMask(digits, withTime) : null);
      } else if (el.value) {
        write('');
      }
      return;
    }
    if (['/', '.', '-', ':', ' '].includes(e.key)) { e.preventDefault(); return; }
    if (e.key === 'Escape') reset();
  };
  const onBlur = () => {
    if (withTime && digits.length === 8) {
      const day = typedToValue(digits, false);
      if (day) { digits = ''; onDraft(null); write(joinDateTime(day, defaultTime(el.value, now()))); return; }
    }
    reset();
  };
  el.addEventListener('keydown', onKey);
  el.addEventListener('blur', onBlur);
  return () => {
    el.removeEventListener('keydown', onKey);
    el.removeEventListener('blur', onBlur);
  };
}

/** Writes a value the way the calendar does: native setter + input/change events (React sees it). */
export function writeNativeValue(input: HTMLInputElement, value: string): void {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  setter?.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}
